import { ResizablePanel, ResizablePanelGroup, Sidebar, SidebarProvider } from '@ibsys/kit';
import { CSSProperties, FC, useCallback, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ConversationListItem, CONVERSATION_TAB_PARAM } from '@/entities/conversations';
import { DialogDataSection } from '@/features/DialogDataSection';
import { DialogTranscriptSection } from '@/features/DialogTranscriptSection';
import { DialogDataResizeHandle } from '../DialogDataResizeHandle';
import { DialogListSection, type DialogListSectionProps } from '../dialogsListSection/DialogListSection';
import { useDialogDataVisibility } from '../model/useDialogDataVisibility';
import { useDialogsSectionRoute } from '../model/useDialogsSectionRoute';

type Props = {
  listSection?: FC<DialogListSectionProps>;
};

export const DialogsOnePageLayout: FC<Props> = ({ listSection }) => {
  const { id } = useParams<{ id: string }>();
  const ListSection = listSection ?? DialogListSection;
  const [searchParams] = useSearchParams();
  const tab = searchParams.get(CONVERSATION_TAB_PARAM) ?? undefined;
  const navigate = useNavigate();
  const location = useLocation();
  const { listPath, navigateToRecord } = useDialogsSectionRoute();
  const { isDataHidden, handleDataResize } = useDialogDataVisibility();

  // Блок управления списком отдаётся не колонке списка, а полосе над обеими панелями: в колонке
  // ему хватает ширины лишь на четыре строки, а над панелями он умещается в одну. Узел полосы
  // держится в состоянии, а не в `useRef`: портал строится во время рендера, а ref заполняется
  // после него — рендер с ref увидел бы null, и второго рендера уже ничто бы не вызвало
  const [controlsContainer, setControlsContainer] = useState<HTMLDivElement | null>(null);

  const handleBack = useCallback(() => {
    // Источник перехода запоминается в state как полный путь возврата (дашборд,
    // выборка, список диалогов). Его нет — возвращаемся к списку текущего раздела.
    navigate(typeof location.state === 'string' ? location.state : listPath);
  }, [navigate, location.state, listPath]);

  const handleTabChange = useCallback(
    (newTab: string) => {
      // Прочие query-параметры (совпадение поиска, id комментария) и секция атрибутов
      // из hash сохраняются — меняется только активный таб.
      navigateToRecord(id ?? null, {
        query: { [CONVERSATION_TAB_PARAM]: newTab },
        saveParams: true,
        hash: location.hash,
        replace: true,
      });
    },
    [id, navigateToRecord, location.hash],
  );

  const handleLoaded = useCallback(
    (conversations: ConversationListItem[]) => {
      if (!id) {
        const firstConversation = conversations[0];
        if (firstConversation) {
          navigateToRecord(firstConversation.id.toString());
        }
      }
    },
    [id, navigateToRecord],
  );

  return (
    // Промежуток до списка держит плотность блока управления: он сужается вместе с промежутками
    // внутри блока (метку плотности блок несёт с собой), а свой отступ панель списка добавляет
    // к нему — поэтому шкала здесь на шаг ниже, чем в блоке
    <div className="flex flex-col h-full relative p-1 gap-3 has-[[data-controls-dense]]:gap-1">
      <div ref={setControlsContainer} className="px-1 empty:hidden" />

      <SidebarProvider
        style={{ ['--sidebar-width']: '380px', ['--sidebar-width-icon']: '60px' } as CSSProperties}
        className="grid-cols-3 grid-rows-1  relative min-h-0 w-full flex-1"
      >
        <ResizablePanelGroup direction="horizontal" className={'gap-3'} storageName={'dialogs-page'}>
          <ResizablePanel minSize={'40%'} defaultSize={'40%'} className={'p-1'} id={'list'}>
            <ListSection controlsContainer={controlsContainer} onLoaded={handleLoaded} />
          </ResizablePanel>
          <DialogDataResizeHandle isDataHidden={isDataHidden} />
          <ResizablePanel className={'p-1'} id={'data'} onResize={handleDataResize}>
            {id && (
              <DialogDataSection
                dialogId={+id}
                onBack={handleBack}
                hideBackButton
                activeTab={tab}
                onTabChange={handleTabChange}
              />
            )}
          </ResizablePanel>
        </ResizablePanelGroup>

        <Sidebar
          side={'right'}
          variant={'floating'}
          className={'static h-full py-1'}
          collapsible={'icon'}
          classNames={{ inner: 'ring-0! bg-transparent shadow-none!' }}
        >
          {id && <DialogTranscriptSection dialogId={+id} />}
        </Sidebar>
      </SidebarProvider>
    </div>
  );
};
